import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

// Lazy load views for optimization
const Shell = lazy(() => import('./components/layout/Shell'));
const KnowledgeView = lazy(() => import('./views/Knowledge/KnowledgeView'));
const PersonaView = lazy(() => import('./views/Personas/PersonaView'));
const SkillsView = lazy(() => import('./views/Skills/SkillsView'));
const RulesView = lazy(() => import('./views/Rules/RulesView'));
const ContextView = lazy(() => import('./views/Context/ContextView'));
import FactDerivationView from './views/FactDerivation/FactDerivationView';
const HotCacheView = lazy(() => import('./views/HotCache/HotCacheView'));
const SimulationsView = lazy(() => import('./views/Simulations/SimulationsView'));
const InsightsView = lazy(() => import('./views/Insights/InsightsView'));
const QualityView = lazy(() => import('./views/Quality/QualityView'));
const SettingsView = lazy(() => import('./views/Settings/SettingsView'));
const IntegrationsView = lazy(() => import('./views/Integrations/IntegrationsView'));
const SecurityView = lazy(() => import('./views/Security/SecurityView'));
const InsightsHelpView = lazy(() => import('./views/Help/InsightsHelpView'));
const TraceEvolutionView = lazy(() => import('./views/TraceEvolution/TraceEvolutionView'));
const HelpView = lazy(() => import('./views/Help/HelpView'));
const SkillDetailsView = lazy(() => import('./views/Help/SkillDetailsView'));
const ContextHelpView = lazy(() => import('./views/Help/ContextHelpView'));
const LandingView = lazy(() => import('./views/Landing/LandingView'));
const UnauthorizedView = lazy(() => import('./views/Unauthorized/UnauthorizedView'));

/**
 * LogoutRoute — visited by Project 1 during its logout flow.
 * Clears Memory Hub's sessionStorage tokens so the user is signed out here too.
 *
 * Project 1 logout handler should do:
 *   window.location.href = 'http://localhost:3001/logout';
 *   // then Keycloak logout continues on Project 1 side
 */
const LogoutRoute: React.FC = () => {
  const DE_URL = import.meta.env.VITE_DE_URL || 'http://localhost:3000';
  React.useEffect(() => {
    sessionStorage.removeItem('mh_kc_token');
    sessionStorage.removeItem('mh_kc_refresh_token');
    localStorage.setItem('mh_logout_signal', String(Date.now()));
    window.location.replace(`${DE_URL}?action=logout`);
  }, [DE_URL]);
  return null;
};

import { ThemeProvider } from './infrastructure/ThemeContext';
import { AuthProvider } from './auth/AuthContext';
import ProtectedRoute from './auth/ProtectedRoute';

import Loader from './components/common/Loader';

const App = () => {
  console.log('[Memory Hub] App: Mounting root component');
  return (
    <ThemeProvider>
      {/* AuthProvider must wrap BrowserRouter so auth state resolves before
          any route renders. It handles:
          A) ?kc_enc_token=<encrypted> query param (AES-256-GCM, from Project 1)
          B) ?kc_token=<jwt> query param (plain, legacy fallback)
          C) localStorage re-hydration (reload / new tab same origin)
          Keycloak SSO is intentionally not used — Project 1 is the only entry point. */}
      <AuthProvider>
        <BrowserRouter>
          <Suspense fallback={<Loader fullScreen />}>
            <Routes>
              {/* Public error routes — no auth required */}
              <Route
                path="/unauthorized"
                element={<UnauthorizedView reason="unauthenticated" />}
              />
              <Route
                path="/forbidden"
                element={<UnauthorizedView reason="forbidden" />}
              />
              {/* Cross-origin logout — called by Project 1 during its logout flow */}
              <Route path="/logout" element={<LogoutRoute />} />

              {/* Protected app shell — only admin users reach this */}
              <Route
                path="/app"
                element={
                  <ProtectedRoute>
                    <Shell />
                  </ProtectedRoute>
                }
              >
                <Route index element={<LandingView />} />
                <Route path="knowledge" element={<KnowledgeView />} />
                <Route path="skills" element={<SkillsView />} />
                <Route path="personas" element={<PersonaView />} />
                <Route path="rules" element={<RulesView />} />
                <Route path="context" element={<ContextView />} />
                <Route path="hot-cache" element={<HotCacheView />} />
                <Route path="derivation" element={<FactDerivationView />} />
                <Route path="trace" element={<TraceEvolutionView />} />
                <Route path="simulations" element={<SimulationsView />} />
                <Route path="insights" element={<InsightsView />} />
                <Route path="quality" element={<Navigate to="/app/insights" replace />} />
                <Route path="settings" element={<SettingsView />} />
                <Route path="integrations" element={<IntegrationsView />} />
                <Route path="security" element={<SecurityView />} />
                <Route path="help" element={<HelpView />} />
                <Route path="help/skills" element={<SkillDetailsView />} />
                <Route path="help/context" element={<ContextHelpView />} />
                <Route path="help/insights" element={<InsightsHelpView />} />
              </Route>

              <Route path="*" element={<Navigate to="/app" replace />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
